import { apiUrls } from '../config/api';

export interface VideoPackage {
  id: number;
  name: string;
  description: string;
  price: number;
  discount_price?: number;
  duration_months: number;
  max_users: number;
  image?: string;
  features?: string;
  book_associations?: Array<{
    book_id: number;
    sort_order: number;
  }>;
}

export interface UserSubscription {
  id: number;
  user_id: number;
  package_id: number;
  start_date: string;
  end_date: string;
  amount_paid: number;
  payment_method: 'card' | 'paypal' | 'manual';
  txn_reference?: string;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
  auto_renewal: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionRequest {
  user_id: number;
  package_id: number;
  start_date: string;
  end_date: string;
  amount_paid: number;
  payment_method: 'card' | 'paypal' | 'manual';
  txn_reference?: string;
}

class SubscriptionService {
  private static instance: SubscriptionService;

  static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  // Get video packages that include a specific book
  async getPackagesForBook(bookId: number): Promise<VideoPackage[]> {
    try {
      // First get all packages
      const allPackagesResponse = await fetch(apiUrls.packages.list, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!allPackagesResponse.ok) {
        throw new Error('Failed to fetch packages');
      }

      const allPackagesData = await allPackagesResponse.json();
      const allPackages = allPackagesData.packages || [];
      
      // Filter packages that include this book
      const packagesForBook = allPackages.filter((pkg: any) => {
        // Check if this book is in the package's book associations
        const hasBook = pkg.packageBooks?.some((pb: any) => 
          pb.book?.id === bookId || pb.book_id === bookId
        ) || pkg.book_associations?.some((ba: any) => 
          ba.book_id === bookId
        );
        
        if (hasBook) {
          console.log(`📦 Package ${pkg.name} contains book ${bookId}:`, {
            packageId: pkg.id,
            packageName: pkg.name,
            price: pkg.price,
            discountPrice: pkg.discount_price,
            packageBooks: pkg.packageBooks
          });
        }
        
        return hasBook;
      });

      console.log(`📦 Found ${packagesForBook.length} packages for book ${bookId}:`, packagesForBook);
      return packagesForBook;
    } catch (error) {
      console.error('Error fetching packages for book:', error);
      return [];
    }
  }

  // Get all available packages
  async getAvailablePackages(): Promise<VideoPackage[]> {
    try {
      const response = await fetch(apiUrls.packages.list, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch packages');
      }

      const data = await response.json();
      return data.packages || [];
    } catch (error) {
      console.error('Error fetching packages:', error);
      return [];
    }
  }

  // Get user's subscriptions
  async getUserSubscriptions(userId: number): Promise<UserSubscription[]> {
    try {
      const response = await fetch(apiUrls.subscriptions.byUser(userId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user subscriptions');
      }

      const data = await response.json();
      return data.subscriptions || [];
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      return [];
    }
  }

  // Check if user has access to a book through any subscription
  async hasBookAccess(userId: number, bookId: number): Promise<boolean> {
    try {
      const response = await fetch(apiUrls.subscriptions.userContent(userId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const accessibleBooks = data.books || [];
      
      return accessibleBooks.some((book: any) => book.id === bookId);
    } catch (error) {
      console.error('Error checking book access:', error);
      return false;
    }
  }

  // Create a new subscription
  async createSubscription(subscriptionData: SubscriptionRequest): Promise<UserSubscription | null> {
    try {
      const response = await fetch(apiUrls.subscriptions.create, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscriptionData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const data = await response.json();
      return data.subscription;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  // Cancel a subscription
  async cancelSubscription(subscriptionId: number): Promise<boolean> {
    try {
      const response = await fetch(apiUrls.subscriptions.cancel(subscriptionId), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return false;
    }
  }

  // Calculate subscription end date based on duration
  calculateEndDate(startDate: string, durationMonths: number): string {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + durationMonths);
    return end.toISOString().split('T')[0]; // Return YYYY-MM-DD format
  }

  // Get effective price (considering discounts)
  getEffectivePrice(price: number, discountPrice?: number): number {
    const safePrice = Number(price) || 0;
    const safeDiscountPrice = discountPrice ? Number(discountPrice) : undefined;
    return safeDiscountPrice && safeDiscountPrice > 0 ? safeDiscountPrice : safePrice;
  }

  // Format subscription for display
  formatSubscription(subscription: UserSubscription): string {
    const endDate = new Date(subscription.end_date);
    const isActive = subscription.status === 'active' && endDate >= new Date();
    
    return `${subscription.status.toUpperCase()} - Expires: ${endDate.toLocaleDateString()}`;
  }
}

export const subscriptionService = SubscriptionService.getInstance();
